/**
 * Versandzeitpunkt eines Bewertungsbogens — Regeln ohne React, damit sie
 * ohne Rendering prüfbar sind.
 *
 * **Warum das eine eigene Stelle braucht.** Der Auslöser besteht aus zwei
 * Teilen: der *Art* („bei einem Statuswechsel") und der *Referenz* („bei
 * welchem Status"). Fehlt die Referenz, versendet DiNo nie — und sagt es
 * nicht: `shouldDispatchOnEvent` in `ratingFormTrigger.py` steigt bei einer
 * fehlenden Referenz wortlos aus.
 *
 * Genau dieser Zustand liess sich bisher herstellen, ohne etwas falsch zu
 * machen (QS qs/dino-qs#9, TF16): jeder Wechsel der Auslöser-Art leerte die
 * Referenzen, und beim Zurückwechseln blieb das Feld leer. Wer die vier
 * Optionen einmal durchklickte und dann speicherte, hatte den automatischen
 * Versand abgeschaltet. Schlimmer noch: ein gespeicherter Vorgangs-Override
 * überstimmt die globale Standardvorgabe — der Versand war danach also auch
 * nicht mehr über den Standard erreichbar.
 */

export type TriggerType = 'never' | 'state' | 'phase' | 'requirement';

export interface ITriggerValue {
  idRatingForm: number | null;
  TriggerType: TriggerType;
  idLegalTransactionState: number | null;
  /** Instanz-ID der Phase im Vorgang. */
  idLegalPhase: number | null;
  /** Instanz-ID der Maßnahme im Vorgang. */
  idLegalRequirement: number | null;
}

/** Standard-Zeitpunkt: Versand beim Abschluss des Vorgangs.
 *  Spiegelt `DEFAULT_TRIGGER_STATE_ID` in `ratingFormTrigger.py`. */
export const DEFAULT_TRIGGER_STATE_ID = 100;

/** Fehlt der Wert? Deckt beides ab — nicht gesetzt und ausdrücklich geleert. */
const isMissing = (value: unknown): boolean =>
  value === null || value === undefined;

/** Feld, das die Auslöser-Art als Referenz braucht. `null` bei „never". */
export const triggerReferenceField = (
  type: TriggerType
): keyof ITriggerValue | null => {
  switch (type) {
    case 'state':
      return 'idLegalTransactionState';
    case 'phase':
      return 'idLegalPhase';
    case 'requirement':
      return 'idLegalRequirement';
    default:
      return null;
  }
};

/**
 * Wechsel der Auslöser-Art.
 *
 * Alle Referenzen bleiben erhalten — wer zwischen den Optionen vergleicht,
 * soll seine Auswahl wiederfinden. Welche Referenz am Ende zählt, entscheidet
 * ohnehin das Backend: `setTransactionOverride` schreibt nur die zur
 * gespeicherten Art passende und setzt die übrigen auf NULL.
 *
 * Für den Status gibt es eine Standardvorgabe (Abschluss des Vorgangs); ist
 * noch keiner gewählt, wird sie eingesetzt. Für Phase und Maßnahme gibt es
 * keine — dort bleibt das Feld leer und das Speichern wird blockiert.
 */
export const applyTriggerTypeChange = (
  value: ITriggerValue,
  nextType: TriggerType
): ITriggerValue => ({
  ...value,
  TriggerType: nextType,
  idLegalTransactionState:
    nextType === 'state' && isMissing(value.idLegalTransactionState)
      ? DEFAULT_TRIGGER_STATE_ID
      : value.idLegalTransactionState,
});

/** Erläuterung unter dem Referenzfeld. Nennt die Standardvorgabe nur da, wo
 *  es eine gibt — sonst wäre das Fehlen eine stille Lücke. */
export const triggerReferenceHelperText = (type: TriggerType): string => {
  switch (type) {
    case 'state':
      return 'Bei welchem Statuswechsel soll der Bogen versendet werden? Standardvorgabe: Abgeschlossen.';
    case 'phase':
      return 'Sobald diese Phase im Vorgang abgeschlossen wird, wird der Bogen versendet. Eine Standardvorgabe gibt es hier nicht — die Phasen sind je Vorgang andere.';
    case 'requirement':
      return 'Sobald diese Maßnahme auf „Erledigt" gesetzt wird, wird der Bogen versendet. Eine Standardvorgabe gibt es hier nicht — die Maßnahmen sind je Vorgang andere.';
    default:
      return '';
  }
};

const MISSING_REFERENCE_MESSAGE: Record<string, string> = {
  state:
    'Bitte einen Status auswählen. Ohne Status wird der Bewertungsbogen nie automatisch versendet.',
  phase:
    'Bitte eine Phase auswählen. Ohne Phase wird der Bewertungsbogen nie automatisch versendet.',
  requirement:
    'Bitte eine Maßnahme auswählen. Ohne Maßnahme wird der Bewertungsbogen nie automatisch versendet.',
};

/**
 * Was dem Speichern entgegensteht — als Satz für den Anwender, oder `null`.
 *
 * Bewusst mit der Folge im Text: „Bitte einen Status auswählen" allein sagt
 * nicht, dass die Einstellung sonst wirkungslos bleibt.
 */
export const triggerSaveProblem = (value: ITriggerValue): string | null => {
  if (value.TriggerType === 'never') return null;

  if (isMissing(value.idRatingForm)) {
    return 'Bitte einen Bewertungsbogen auswählen — ohne Bogen kann nichts versendet werden.';
  }

  const field = triggerReferenceField(value.TriggerType);
  if (field && isMissing(value[field])) {
    return MISSING_REFERENCE_MESSAGE[value.TriggerType] ?? null;
  }
  return null;
};

/** Gespeicherte (oder effektive) Konfiguration, wie das Backend sie liefert. */
export interface IStoredTrigger {
  TriggerType?: string | null;
  idRatingForm?: number | null;
  idLegalTransactionState?: number | null;
  idLegalPhase?: number | null;
  idLegalRequirement?: number | null;
}

/**
 * Hinweis für eine **bereits gespeicherte** Konfiguration, die nie auslösen
 * kann. Betrifft Bestandsdaten: vor der Absicherung liess sich genau das
 * speichern, und es sah in der Maske vollständig aus.
 *
 * Bewusst nur ein Hinweis: die gespeicherte Konfiguration wird nicht
 * stillschweigend umgedeutet. Ein Vorgang, für den der Notar meint, es
 * passiere nichts, soll nicht plötzlich Mails verschicken — er soll erfahren,
 * dass die Einstellung unwirksam ist, und selbst entscheiden.
 */
export const ineffectiveTriggerWarning = (
  stored?: IStoredTrigger | null
): string | null => {
  if (!stored) return null;
  const type = stored.TriggerType;
  if (!type || type === 'never') return null;

  if (isMissing(stored.idRatingForm)) {
    return 'Der automatische Versand ist derzeit unwirksam: es ist kein Bewertungsbogen hinterlegt.';
  }

  const field = triggerReferenceField(type as TriggerType);
  if (!field) return null;
  if (!isMissing(stored[field as keyof IStoredTrigger])) return null;

  const what =
    type === 'state' ? 'Status' : type === 'phase' ? 'Phase' : 'Maßnahme';
  return `Der automatische Versand ist derzeit unwirksam: als Auslöser ist „${what}" eingestellt, aber keine Auswahl getroffen. Bitte ergänzen und speichern.`;
};

/**
 * Datensatz für den PUT.
 *
 * Leere Werte werden weggelassen, nicht als `null` geschickt: das
 * Body-Schema (`modules/ratingForm.json`) deklariert die Referenzen als
 * `integer`, ein `null` würde die Anfrage abweisen. Und es wird nur die
 * Referenz der aktiven Art übertragen — die übrigen setzt das Backend
 * ohnehin auf NULL.
 */
export const buildTriggerBody = (
  value: ITriggerValue
): Record<string, unknown> => {
  const body: Record<string, unknown> = { TriggerType: value.TriggerType };
  if (!isMissing(value.idRatingForm)) body.idRatingForm = value.idRatingForm;

  const field = triggerReferenceField(value.TriggerType);
  if (field && !isMissing(value[field])) {
    body[field] = value[field];
  }
  return body;
};
